import { useCallback, useEffect, useRef, useState } from "react"

import type { CloseLoopPlaceholder } from "@productlane/lib/src/close-loop-template"
import { closeLoopPlaceholderToken } from "@productlane/lib/src/close-loop-template"
import { cn } from "@productlane/lib/src/cn"

import type {
	SuggestionKeyDownProps,
	SuggestionProps,
} from "@tiptap/suggestion"

export type PlaceholderKeyDownHandler = (
	props: SuggestionKeyDownProps,
) => boolean

export type PlaceholderListProps = SuggestionProps<
	CloseLoopPlaceholder,
	{ key: string }
> & {
	setOnKeyDownHandler: (handler: PlaceholderKeyDownHandler) => void
	formatToken?: (key: string) => string
}

export default function PlaceholderList({
	items,
	command,
	setOnKeyDownHandler,
	formatToken = closeLoopPlaceholderToken,
}: PlaceholderListProps) {
	const [selected, setSelected] = useState(0)
	const selectedRef = useRef(0)

	const moveSelection = useCallback((index: number) => {
		selectedRef.current = index
		setSelected(index)
	}, [])

	useEffect(() => moveSelection(0), [items, moveSelection])

	const select = useCallback(
		(index: number) => {
			const item = items[index]
			if (item) command({ key: item.key })
		},
		[command, items],
	)

	useEffect(() => {
		setOnKeyDownHandler(({ event }) => {
			if (items.length === 0) return false
			switch (event.key) {
				case "ArrowUp":
					moveSelection((selectedRef.current + items.length - 1) % items.length)
					return true
				case "ArrowDown":
					moveSelection((selectedRef.current + 1) % items.length)
					return true
				case "Enter":
				case "Tab":
					select(selectedRef.current)
					return true
				default:
					return false
			}
		})
	}, [items, moveSelection, select, setOnKeyDownHandler])

	if (items.length === 0) return null

	return (
		<div className="max-h-64 min-w-72 overflow-y-auto rounded-lg bg-white p-1 pb-2 shadow-md ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<ul className="m-0 flex list-none flex-col p-0">
				{items.map((item, index) => (
					<li key={item.key}>
						<button
							type="button"
							onClick={() => select(index)}
							onMouseMove={() => moveSelection(index)}
							className={cn(
								"flex w-full cursor-default items-center gap-3 rounded-md px-2 py-1.5 text-left",
								selected === index && "bg-gray-100 dark:bg-gray-700/50",
							)}
						>
							<span className="flex-none font-mono text-xs text-secondary">
								{formatToken(item.key)}
							</span>
							<span className="ml-auto truncate text-xs text-tertiary">
								{item.label}
							</span>
						</button>
					</li>
				))}
			</ul>
		</div>
	)
}
